import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ArrowLeft, Download, MoreHorizontal, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/lib/toast";

import apiClient from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  useAdminUsers,
  useDeactivateUser,
  useRestoreUser,
  useSetUserPlan,
  type AdminUser,
} from "@/hooks/useAdminUsers";
import UserDetailDrawer, {
  Avatar,
  relativeTime,
} from "@/components/admin/UserDetailDrawer";
import { ConfirmDeleteUserModal } from "@/components/admin/ConfirmDeleteUserModal";
import { GrantCompDialog } from "@/components/admin/GrantCompDialog";
import { EditTagsPopover } from "@/components/admin/EditTagsPopover";
import { PlanNotesDialog } from "@/components/admin/PlanNotesDialog";
import { ActAsDialog } from "@/components/admin/ActAsDialog";
import { ConfirmResetUserFeedbackModal } from "@/components/admin/ConfirmResetUserFeedbackModal";

export const Route = createFileRoute("/admin/users")({
  component: AdminUsersPage,
});

const PLAN_OPTIONS = ["free", "student", "beta", "pro", "institutional"] as const;

type ChipId =
  | "all"
  | "paid"
  | "free"
  | "deactivated"
  | "deleted"
  | "internal"
  | "beta"
  | "friend";

interface Chip {
  id: ChipId;
  label: string;
}

const CHIPS: Chip[] = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "free", label: "Free" },
  { id: "deactivated", label: "Deactivated" },
  { id: "deleted", label: "Deleted" },
  { id: "internal", label: "Internal" },
  { id: "beta", label: "Beta" },
  { id: "friend", label: "Friend" },
];

function applyChip(users: AdminUser[], chip: ChipId): AdminUser[] {
  switch (chip) {
    case "all":
      return users;
    case "paid":
      return users.filter((u) => u.plan !== "free");
    case "free":
      return users.filter((u) => u.plan === "free");
    case "deactivated":
      // True deactivations only — tombstones also set deactivated_at,
      // but they belong under the Deleted chip.
      return users.filter(
        (u) => u.deactivated_at !== null && u.deleted_at === null,
      );
    case "deleted":
      return users.filter((u) => u.deleted_at !== null);
    case "internal":
    case "beta":
    case "friend":
      return users.filter((u) => u.tags.includes(chip));
    default:
      return users;
  }
}

export function AdminUsersPage() {
  const router = useRouter();

  // Controlled search input; debouncedQ drives the query.
  const [rawQ, setRawQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(rawQ), 300);
    return () => clearTimeout(t);
  }, [rawQ]);

  const [chip, setChip] = useState<ChipId>("all");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // Dialog targets. A null value means the dialog is closed. We track the
  // target user per dialog (rather than a single selection) so the user can
  // open one dialog while a different one is animating out.
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [grantCompUser, setGrantCompUser] = useState<AdminUser | null>(null);
  const [editTagsUser, setEditTagsUser] = useState<AdminUser | null>(null);
  const [planNotesUser, setPlanNotesUser] = useState<AdminUser | null>(null);
  const [actAsUser, setActAsUser] = useState<AdminUser | null>(null);
  const [resetFeedbackUser, setResetFeedbackUser] = useState<AdminUser | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadCsv() {
    setDownloading(true);
    try {
      const blob = await apiClient
        .get("api/admin/users/export.csv", { timeout: 60_000 })
        .blob();
      const url = URL.createObjectURL(blob);
      const ts = new Date()
        .toISOString()
        .replace(/[-:]/g, "")
        .slice(0, 15);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paperchase-users-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to download user CSV",
      );
    } finally {
      setDownloading(false);
    }
  }

  const { data: users, isLoading, error } = useAdminUsers({
    q: debouncedQ,
    includeDeleted: chip === "deleted",
  });
  const setPlan = useSetUserPlan();
  const deactivate = useDeactivateUser();
  const restore = useRestoreUser();

  const filteredUsers = useMemo(
    () => (users ? applyChip(users, chip) : []),
    [users, chip],
  );

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => router.history.back()}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-2xl font-bold">User Management</h1>
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCsv}
            disabled={downloading}
          >
            <Download className="size-4 mr-2" />
            {downloading ? "Downloading…" : "Download CSV"}
          </Button>
        </div>
      </div>

      {/* Search + chips */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <Input
            value={rawQ}
            onChange={(e) => setRawQ(e.target.value)}
            placeholder="Search by name, email, auth_id, or cus_ ID…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {CHIPS.map((c) => {
            const active = chip === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setChip(c.id)}
                className={
                  "rounded-full border px-3 py-1 text-xs transition-colors " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground hover:bg-muted")
                }
                aria-pressed={active}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">
            {error instanceof Error ? error.message : "Failed to load users"}
          </CardContent>
        </Card>
      )}

      {isLoading && <p className="text-muted-foreground">Loading users...</p>}

      {users && (
        <Card>
          <CardHeader>
            <CardTitle>
              Users ({filteredUsers.length}
              {filteredUsers.length !== users.length
                ? ` of ${users.length}`
                : ""}
              )
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium w-10" />
                    <th className="pb-2 font-medium">Name</th>
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium">Plan</th>
                    <th className="pb-2 font-medium text-center">Concepts</th>
                    <th className="pb-2 font-medium">Last Active</th>
                    <th className="pb-2 font-medium">Joined</th>
                    <th className="pb-2 font-medium w-10" />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const fullName = [user.first_name, user.last_name]
                      .filter(Boolean)
                      .join(" ");
                    const displayName =
                      fullName || user.display_name || user.auth_id;
                    const isDeleted = user.deleted_at !== null;
                    const isDeactivated =
                      user.deactivated_at !== null && !isDeleted;

                    return (
                      <tr
                        key={user.id}
                        className={
                          "border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors " +
                          (isDeleted || isDeactivated ? "opacity-60" : "")
                        }
                        onClick={() => setSelectedUser(user)}
                      >
                        <td className="py-2">
                          <Avatar user={user} size={32} />
                        </td>
                        <td className="py-2 font-medium">
                          <div className="flex items-center gap-2">
                            <span>{displayName}</span>
                            {user.tags.map((t) => (
                              <span
                                key={t}
                                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                              >
                                {t}
                              </span>
                            ))}
                            {isDeleted && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground">
                                deleted
                              </span>
                            )}
                            {isDeactivated && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">
                                deactivated
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 font-mono text-xs text-muted-foreground">
                          {user.email}
                        </td>
                        <td className="py-2">
                          <select
                            value={user.plan}
                            disabled={isDeleted}
                            onChange={(e) =>
                              setPlan.mutate({
                                userId: user.id,
                                plan: e.target.value,
                              })
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border bg-background px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {PLAN_OPTIONS.map((p) => (
                              <option key={p} value={p}>
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 text-center">
                          {user.concept_count}
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {relativeTime(user.last_active_at)}
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td
                          className="py-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <UserRowKebab
                            user={user}
                            onActAs={() => setActAsUser(user)}
                            onDeactivate={() => deactivate.mutate(user.id)}
                            onRestore={() => restore.mutate(user.id)}
                            onDelete={() => setDeleteUser(user)}
                            onGrantComp={() => setGrantCompUser(user)}
                            onEditTags={() => setEditTagsUser(user)}
                            onPlanNotes={() => setPlanNotesUser(user)}
                            onResetFeedback={() => setResetFeedbackUser(user)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <UserDetailDrawer
        user={selectedUser}
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />

      <ConfirmDeleteUserModal
        user={deleteUser}
        open={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        onDeleted={(result) => {
          if (result.r2_errors.length > 0) {
            toast.warning(
              `User deleted with ${result.r2_errors.length} R2 error(s).`,
            );
          } else {
            toast.success("User deleted.");
          }
        }}
      />

      <GrantCompDialog
        user={grantCompUser}
        open={!!grantCompUser}
        onClose={() => setGrantCompUser(null)}
      />

      <EditTagsPopover
        user={editTagsUser}
        open={!!editTagsUser}
        onClose={() => setEditTagsUser(null)}
      />

      <PlanNotesDialog
        user={planNotesUser}
        open={!!planNotesUser}
        onClose={() => setPlanNotesUser(null)}
      />

      <ActAsDialog
        user={actAsUser}
        open={!!actAsUser}
        onClose={() => setActAsUser(null)}
      />

      <ConfirmResetUserFeedbackModal
        user={resetFeedbackUser}
        open={!!resetFeedbackUser}
        onClose={() => setResetFeedbackUser(null)}
        onReset={(deleted) => {
          toast.success(
            `Deleted ${deleted} feedback event${deleted === 1 ? "" : "s"}.`
          );
          setResetFeedbackUser(null);
        }}
      />
    </div>
  );
}

function UserRowKebab({
  user,
  onActAs,
  onDeactivate,
  onRestore,
  onDelete,
  onGrantComp,
  onEditTags,
  onPlanNotes,
  onResetFeedback,
}: {
  user: AdminUser;
  onActAs: () => void;
  onDeactivate: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onGrantComp: () => void;
  onEditTags: () => void;
  onPlanNotes: () => void;
  onResetFeedback: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isDeleted = user.deleted_at !== null;
  const isDeactivated = user.deactivated_at !== null && !isDeleted;

  function run(fn: () => void) {
    setOpen(false);
    fn();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`kebab-${user.id}`}
          className="rounded p-1 hover:bg-muted transition-colors"
          aria-label="User actions"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-40 p-1 text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {isDeleted ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Tombstoned — actions disabled. Audit history remains in the
            detail drawer.
          </div>
        ) : (
          <>
            <MenuItem onClick={() => run(onActAs)}>Act as</MenuItem>
            <MenuItem onClick={() => run(onGrantComp)}>Grant Comp</MenuItem>
            <MenuItem onClick={() => run(onEditTags)}>Edit Tags</MenuItem>
            <MenuItem onClick={() => run(onPlanNotes)}>Plan Notes</MenuItem>
            <div className="-mx-1 my-1 h-px bg-border" />
            {isDeactivated ? (
              <MenuItem onClick={() => run(onRestore)}>Restore</MenuItem>
            ) : (
              <MenuItem onClick={() => run(onDeactivate)}>Deactivate</MenuItem>
            )}
            <MenuItem onClick={() => run(onResetFeedback)}>Reset feedback events</MenuItem>
            <MenuItem destructive onClick={() => run(onDelete)}>
              Delete
            </MenuItem>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function MenuItem({
  children,
  onClick,
  destructive,
}: {
  children: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={
        "w-full text-left rounded-sm px-2 py-1.5 transition-colors hover:bg-accent hover:text-accent-foreground " +
        (destructive ? "text-destructive hover:bg-destructive/10" : "")
      }
    >
      {children}
    </button>
  );
}
